import { Component, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { DeliveryOption } from 'libs/shared-models/src/lib/delivery-option';
import { OrderStatus } from 'libs/shared-models/src/lib/order/order-response';
import { RestaurantResponse } from 'libs/shared-models/src/lib/restaurant/restaurant-response';
import { LOADING_STYLE } from 'libs/shared-ui/src/lib/fs-loading-spinner/fs-loading-spinner.component';
import { LocaleService } from 'libs/shared-services/src/lib/locale.service';

import { RestaurantDashboardService } from '../../../../services/restaurant-dashboard/restaurant-dashboard.service';
import {
  DashboardOrderChannelResponse,
  DashboardOrderResponse,
  DashboardRatingDistributionResponse,
  DashboardTopProductResponse,
  RestaurantDashboardResponse,
} from '../../../../services/restaurant-dashboard/data-store/model/restaurant-dashboard-response';
import { RestaurantInfoService } from '../../../../services/restaurant-info/restaurant-info.service';

@Component({
  selector: 'web-foodis-verified-dashboard',
  templateUrl: './verified-dashboard.component.html',
  styleUrls: ['./verified-dashboard.component.scss'],
})
export class VerifiedDashboardComponent implements OnInit {
  public readonly dashboard$: Observable<RestaurantDashboardResponse | null> =
    this.dashboardService.dashboard$();
  public readonly isLoading$: Observable<boolean> = this.dashboardService.isLoading$();
  public readonly hasError$: Observable<boolean> = this.dashboardService.hasError$();
  public readonly selectedPeriodDays$: Observable<number> =
    this.dashboardService.selectedPeriodDays$();
  public readonly restaurantData$: Observable<RestaurantResponse> =
    this.restaurantInfoService.getState$();

  public readonly availablePeriods: number[] = RestaurantDashboardService.AVAILABLE_PERIOD_DAYS;
  public readonly ratingValues: number[] = [5, 4, 3, 2, 1];
  public readonly starValues: number[] = [1, 2, 3, 4, 5];
  public readonly DeliveryOption: typeof DeliveryOption = DeliveryOption;
  public readonly LOADING_STYLE: typeof LOADING_STYLE = LOADING_STYLE;

  public constructor(
    private readonly dashboardService: RestaurantDashboardService,
    private readonly restaurantInfoService: RestaurantInfoService,
    private readonly localeService: LocaleService
  ) {}

  public ngOnInit(): void {
    this.dashboardService.fetchDashboardAPI(RestaurantDashboardService.DEFAULT_PERIOD_DAYS);
  }

  public refresh(): void {
    this.dashboardService.retry();
  }

  public onPeriodChange(periodDays: number): void {
    this.dashboardService.fetchDashboardAPI(periodDays);
  }

  public getMetricChange(currentValue: number, previousValue: number): number | null {
    if (previousValue === 0) {
      return currentValue === 0 ? 0 : null;
    }

    return Math.round(((currentValue - previousValue) / previousValue) * 100);
  }

  public getMetricTrendClass(currentValue: number, previousValue: number): string {
    if (currentValue > previousValue) {
      return 'increase';
    }
    if (currentValue < previousValue) {
      return 'decrease';
    }
    return 'unchanged';
  }

  public getStatusTranslationKey(status: OrderStatus | string): string {
    switch (status) {
      case OrderStatus.CREATED:
        return 'dashboard_status_created';
      case OrderStatus.PENDING_PAYMENT:
        return 'dashboard_status_pending_payment';
      case OrderStatus.PAYMENT_FAILED:
        return 'dashboard_status_payment_failed';
      case OrderStatus.PAID:
        return 'dashboard_status_paid';
      case OrderStatus.ACCEPTED_BY_RESTAURANT:
        return 'dashboard_status_accepted';
      case OrderStatus.DECLINED_BY_RESTAURANT:
        return 'dashboard_status_declined';
      case OrderStatus.PREPARING:
        return 'dashboard_status_preparing';
      case OrderStatus.READY_FOR_PICKUP:
        return 'dashboard_status_ready';
      case OrderStatus.IN_DELIVERY:
      case OrderStatus.OUT_FOR_DELIVERY:
        return 'dashboard_status_out_for_delivery';
      case OrderStatus.DELIVERED:
        return 'dashboard_status_delivered';
      case OrderStatus.CANCELLED:
        return 'dashboard_status_cancelled';
      case OrderStatus.REFUNDED:
        return 'dashboard_status_refunded';
      default:
        return 'dashboard_status_unknown';
    }
  }

  public getStatusClass(status: OrderStatus | string): string {
    switch (status) {
      case OrderStatus.CREATED:
      case OrderStatus.PAID:
        return 'urgent';
      case OrderStatus.PREPARING:
        return 'preparing';
      case OrderStatus.ACCEPTED_BY_RESTAURANT:
      case OrderStatus.READY_FOR_PICKUP:
      case OrderStatus.IN_DELIVERY:
      case OrderStatus.OUT_FOR_DELIVERY:
        return 'active';
      case OrderStatus.CANCELLED:
      case OrderStatus.DECLINED_BY_RESTAURANT:
      case OrderStatus.PAYMENT_FAILED:
        return 'negative';
      case OrderStatus.DELIVERED:
        return 'completed';
      case OrderStatus.REFUNDED:
        return 'refunded';
      default:
        return 'neutral';
    }
  }

  public getDeliveryOptionTranslationKey(deliveryOption: DeliveryOption | string): string {
    return deliveryOption === DeliveryOption.DELIVERY
      ? 'dashboard_delivery_option_delivery'
      : 'dashboard_delivery_option_pickup';
  }

  public getPaymentTypeTranslationKey(paymentType: string): string {
    return paymentType === 'CASH'
      ? 'dashboard_payment_type_cash'
      : 'dashboard_payment_type_online';
  }

  public getTopProductWidth(
    quantitySold: number,
    products: DashboardTopProductResponse[]
  ): number {
    const maximumQuantity = Math.max(...products.map((product) => product.quantitySold), 0);
    return maximumQuantity > 0 ? Math.max(8, (quantitySold / maximumQuantity) * 100) : 0;
  }

  public getChannelCount(
    breakdown: DashboardOrderChannelResponse[],
    deliveryOption: DeliveryOption
  ): number {
    return breakdown.find((item) => item.deliveryOption === deliveryOption)?.orderCount || 0;
  }

  public getChannelPercentage(
    breakdown: DashboardOrderChannelResponse[],
    deliveryOption: DeliveryOption
  ): number {
    const total = breakdown.reduce((sum, item) => sum + item.orderCount, 0);
    return total > 0 ? Math.round((this.getChannelCount(breakdown, deliveryOption) / total) * 100) : 0;
  }

  public getProductsAvailablePercentage(total: number, available: number): number {
    return total > 0 ? Math.round((available / total) * 100) : 0;
  }

  public formatDateKey(date: string): string {
    const parts = date.split('-');
    return parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : date;
  }

  public formatTimestamp(
    value: string | null | undefined,
    timeZone: string,
    format: DashboardTimestampFormat
  ): string {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    const options: Intl.DateTimeFormatOptions =
      format === 'time'
        ? { hour: '2-digit', minute: '2-digit' }
        : format === 'date'
          ? { day: '2-digit', month: 'short', year: 'numeric' }
          : {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            };

    try {
      return new Intl.DateTimeFormat(this.localeService.getLanguageISO2() || undefined, {
        ...options,
        ...(timeZone ? { timeZone } : {}),
      }).format(date);
    } catch {
      return new Intl.DateTimeFormat(
        this.localeService.getLanguageISO2() || undefined,
        options
      ).format(date);
    }
  }

  public getRatingCount(
    rating: number,
    distribution: DashboardRatingDistributionResponse[]
  ): number {
    return distribution.find((item) => item.rating === rating)?.count || 0;
  }

  public getRatingPercentage(
    rating: number,
    distribution: DashboardRatingDistributionResponse[],
    totalReviews: number
  ): number {
    return totalReviews > 0
      ? Math.round((this.getRatingCount(rating, distribution) / totalReviews) * 100)
      : 0;
  }

  public trackOrder(_index: number, order: DashboardOrderResponse): string {
    return order.id;
  }

  public trackProduct(_index: number, product: DashboardTopProductResponse): string {
    return product.productId;
  }
}

export type DashboardTimestampFormat = 'date' | 'dateTime' | 'time';
